import React, { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useToast } from "@chakra-ui/react";
import { useCollections } from "hooks/useCollections";
import type { RequestEntities } from "types/charliui";
import { sendMessage } from "state/websocket/operations";
import { ToastMessageContent } from "screens/common/components";

interface UseProjectMoveOptions {
  collectionId: string;
  conversationId: string;
}

export const useProjectMove = ({ collectionId, conversationId }: UseProjectMoveOptions) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const collections = useCollections();

  const portfolioCollections = useMemo(
    () => collections.filter((collection) => collection.collectionType === "portfolio" && !collection.shareDetails),
    [collections]
  );

  const projectsInPortfolio = useMemo(() => {
    const allMetadataIds = portfolioCollections.flatMap((portfolio) => portfolio.metadataIds);
    return collections.filter((collection) => allMetadataIds.includes(collection.id));
  }, [collections, portfolioCollections]);

  const fromPortfolioId = useMemo(() => {
    return portfolioCollections.find((collection) => collection.metadataIds.includes(collectionId))?.id;
  }, [collectionId, portfolioCollections]);

  const projectName = projectsInPortfolio.find((collection) => collection.id === collectionId)?.name;
  const fromPortfolioName = portfolioCollections.find((collection) => collection.id === fromPortfolioId)?.name;

  const showToast = useCallback(
    (message: string) => {
      toast.closeAll();
      toast({
        render: ({ onClose }) => React.createElement(ToastMessageContent, { message, onClose }),
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
    [toast]
  );

  const moveToPortfolio = useCallback(
    (toPortfolioId: string) => {
      if (!collectionId) return;

      const toPortfolioName = portfolioCollections.find((collection) => collection.id === toPortfolioId)?.name;

      const entities: RequestEntities = [{ entity: "collection_id", value: collectionId }];
      entities.push({ entity: "to_portfolio_id", value: toPortfolioId });
      if (fromPortfolioId) {
        entities.push({ entity: "from_portfolio_id", value: fromPortfolioId });
      }

      dispatch(
        sendMessage({
          conversationId,
          intent: "/move_project",
          entities,
        })
      );

      showToast(`I'll move the project ${projectName} over to the ${toPortfolioName} portfolio now. This will only take a second or two.`);
    },
    [collectionId, fromPortfolioId, dispatch, conversationId, showToast, projectName, portfolioCollections]
  );

  const removeFromPortfolio = useCallback(() => {
    if (!collectionId || !fromPortfolioId) return;

    const entities: RequestEntities = [{ entity: "collection_id", value: collectionId }];
    entities.push({ entity: "from_portfolio_id", value: fromPortfolioId });

    dispatch(
      sendMessage({
        conversationId,
        intent: "/move_project",
        entities,
      })
    );

    showToast(`I'll remove the project ${projectName} from the ${fromPortfolioName} portfolio now. This will only take a second or two.`);
  }, [collectionId, fromPortfolioId, dispatch, conversationId, showToast, projectName, fromPortfolioName]);

  const isInPortfolio = Boolean(fromPortfolioId);

  return {
    moveToPortfolio,
    removeFromPortfolio,
    isInPortfolio,
    portfolioCollections,
    fromPortfolioId,
    fromPortfolioName,
    projectName,
  };
};
